'use client';

import { useTranslations } from 'next-intl';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  ScanLine,
  XCircle
} from 'lucide-react';

import { API_ENDPOINTS } from '@/lib/api/config';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useRouter } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import {
  MoveToRepairDialog,
  type MoveToRepairPayload
} from '@/features/repair-settings';
import { PHOTO_SCANNING_HASH } from '../constants';
import { useRefuseBatchMutation } from '../services/photo-scanning.mutations';
import type { ScanBatch, BatchPageRecord } from '../types';

type Tone = 'emerald' | 'rose' | 'slate';

const TONE_STYLES: Record<Tone, string> = {
  emerald:
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300',
  rose: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300',
  slate:
    'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-200'
};

interface ScanProgressHeaderProps {
  progressPct: number;
  success: number;
  failed: number;
  remaining: number;
  total: number;
  isComplete: boolean;
  batch: (ScanBatch & { pages: BatchPageRecord[] }) | undefined;
  batchId: number;
  onTransferToQc: () => void;
  isTransferPending: boolean;
  // When the batch has been handed off to QA the page is read-only — the
  // transfer and repair CTAs are suppressed (the PDF download stays).
  isHandedOff?: boolean;
}

export function ScanProgressHeader({
  progressPct,
  success,
  failed,
  remaining,
  total,
  isComplete,
  batch,
  batchId,
  onTransferToQc,
  isTransferPending,
  isHandedOff = false
}: ScanProgressHeaderProps) {
  const t = useTranslations('photoScanning');
  const tRepair = useTranslations('repairs');
  const router = useRouter();
  // Overlay open-state lives in the URL hash (#move-to-repair) so it's
  // deep-linkable and closes on browser Back.
  const { open: moveRepairOpen, onOpenChange: toggleMoveRepair } =
    useHashDialog(PHOTO_SCANNING_HASH.moveToRepair);
  const refuseMutation = useRefuseBatchMutation();

  const handleConfirmRefuse = (data: MoveToRepairPayload) => {
    if (batch?.registration_id == null) return;
    // This dialog never sets `optionalDestination`, so the destination is
    // always required and present here — narrow the shared payload type back
    // to non-optional for the mutation.
    if (data.repair_destination_id == null) return;
    refuseMutation.mutate(
      {
        registrationId: batch.registration_id,
        ...data,
        repair_destination_id: data.repair_destination_id
      },
      {
        onSuccess: () => {
          toggleMoveRepair(false);
          // The container has left the scan-upload stage for the repair
          // queue — leave the execution page so no further scanning actions
          // can be taken on it.
          router.push('/dashboard/photo-scanning');
        }
      }
    );
  };

  return (
    <div className='flex shrink-0 flex-wrap items-center gap-3 border-b bg-slate-50/60 px-4 py-2.5 dark:bg-slate-900/40'>
      {/* Progress bar — stretches with the row */}
      <div className='flex min-w-[220px] flex-1 items-center gap-3'>
        <span className='text-[10px] font-bold tracking-wider text-slate-500 uppercase'>
          {t('scanProgress')}
        </span>
        <Progress value={progressPct} className='h-1.5 flex-1' />
        <span className='text-xs font-bold text-slate-700 tabular-nums dark:text-slate-200'>
          {progressPct}% · {success}/{total}
        </span>
      </div>

      {/* Counter pills */}
      <div className='flex items-center gap-2'>
        <CounterPill
          icon={CheckCircle2}
          value={success}
          tone='emerald'
          label={t('successCounter')}
        />
        <CounterPill
          icon={XCircle}
          value={failed}
          tone='rose'
          label={t('failedCounter')}
        />
        <CounterPill
          icon={ScanLine}
          value={remaining}
          tone='slate'
          label={t('remainingCounter')}
        />
      </div>

      {/* Trailing actions — Move to Repair stays available throughout scanning
          (a damaged container can surface at any point); the completion CTAs
          appear once every slot is a success. */}
      <div className='ms-auto flex items-center gap-2'>
        {!isHandedOff && batch?.registration_id != null && (
          <PermissionGate permissions={['photo_scanning.edit']}>
            <Button
              size='sm'
              variant='outline'
              className='h-8 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/30'
              onClick={() => toggleMoveRepair(true)}
            >
              <AlertTriangle className='h-3.5 w-3.5' />
              {tRepair('moveToRepair')}
            </Button>
          </PermissionGate>
        )}
        {isComplete && (
          <>
            {batch?.pdf_path && (
              <Button
                variant='outline'
                size='sm'
                className='h-8 gap-1.5'
                asChild
              >
                <a
                  href={API_ENDPOINTS.PHOTO_SCANNING.BATCH_PDF(batchId)}
                  download
                >
                  <FileText className='h-3.5 w-3.5' />
                  {t('downloadPdf')}
                </a>
              </Button>
            )}
            {!isHandedOff && (
              <Button
                size='sm'
                className='h-8 bg-emerald-600 text-white hover:bg-emerald-700'
                onClick={onTransferToQc}
                disabled={isTransferPending}
              >
                <CheckCircle2 className='me-1.5 h-3.5 w-3.5' />
                {t('transferToQc')}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Move to repair queue — #move-to-repair */}
      <MoveToRepairDialog
        open={moveRepairOpen}
        onOpenChange={toggleMoveRepair}
        containerNumber={batch?.container_id}
        onConfirm={handleConfirmRefuse}
        isPending={refuseMutation.isPending}
      />
    </div>
  );
}

function CounterPill({
  icon: Icon,
  value,
  label,
  tone
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  label: string;
  tone: Tone;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-md border px-2 py-1',
        TONE_STYLES[tone]
      )}
    >
      <Icon className='h-3 w-3' />
      <span className='text-sm leading-none font-bold tabular-nums'>
        {value}
      </span>
      <span className='hidden text-[10px] font-medium tracking-wider uppercase opacity-80 sm:inline'>
        {label}
      </span>
    </div>
  );
}
